import { SagaReturnType, call, put, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import patientModel, { CreatePatientDto, Patient, UpdatePatientDto } from '@/core/models/patient.model'
import { SearchPatientsFilterParams } from '@/core/types/request'

import {
  getCarteVitaleInfos as getCarteVitaleInfosAction,
  getCarteVitaleInfosSuccess as getCarteVitaleInfosSuccessAction,
  getCarteVitaleInfosFailure as getCarteVitaleInfosFailureAction,
  createPatient as createPatientAction,
  createPatientSuccess as createPatientSuccessAction,
  createPatientFailure as createPatientFailureAction,
  createPatientAccount as createPatientAccountAction,
  createPatientAccountSuccess as createPatientAccountSuccessAction,
  createPatientAccountFailure as createPatientAccountFailureAction,
  getPatient as getPatientAction,
  getPatientSuccess as getPatientSuccessAction,
  getPatientFailure as getPatientFailureAction,
  getPatients as getPatientsAction,
  getPatientsSuccess as getPatientsSuccessAction,
  getPatientsFailure as getPatientsFailureAction,
  updatePatient as updatePatientAction,
  updatePatientSuccess as updatePatientSuccessAction,
  updatePatientFailure as updatePatientFailureAction,
  deletePatient as deletePatientAction,
  deletePatientSuccess as deletePatientSuccessAction,
  deletePatientFailure as deletePatientFailureAction,
  downloadConsent as downloadConsentAction,
  downloadConsentSuccess as downloadConsentSuccessAction,
  downloadConsentFailure as downloadConsentFailureAction,
  getOrCreateOrdoclicPatient as getOrCreateOrdoclicPatientAction,
  getOrCreateOrdoclicPatientSuccess as getOrCreateOrdoclicPatientSuccessAction,
  getOrCreateOrdoclicPatientFailure as getOrCreateOrdoclicPatientFailureAction,
  patientUpdatedSocketEvent,
} from '@/redux/reducers/patient.reducer'

import { handleError } from '@/utils/errors'
import { SearchResponse } from '@/core/types/response'

function* getCarteVitaleInfos() {
  try {
    const carteVitaleInfos: Array<Record<string, string>> = yield call(patientModel.getCarteVitaleInfos)
    yield put(getCarteVitaleInfosSuccessAction(carteVitaleInfos))
    if (carteVitaleInfos.length) {
      const { items, totalCount }: SearchResponse<Patient> = yield call(
        patientModel.getPatients,
        {
          smartSearch: carteVitaleInfos[0].nir.replace(/ /g, ''),
          limit: 4, skip: 0, order: 'ASC', orderBy: 'name',
        }
      )
      yield put(getPatientsSuccessAction({ items, totalCount }))
    }
  } catch (exception) {
    console.warn(exception)
    yield put(getCarteVitaleInfosFailureAction(handleError(exception)))
  }
}

function* createPatient(action: PayloadAction<CreatePatientDto>) {
  try {
    const createPatientDto = action.payload
    const patient: Patient = yield call(patientModel.createPatient, createPatientDto)
    yield put(createPatientSuccessAction(patient))
  } catch (exception) {
    console.warn(exception)
    yield put(createPatientFailureAction(handleError(exception)))
  }
}

function* createPatientAccount(action: PayloadAction<ReturnType<typeof createPatientAccountAction>['payload']>) {
  try {
    const patientId = action.payload
    const { createPatientAccount } = patientModel
    const patient: SagaReturnType<typeof createPatientAccount> = yield call(createPatientAccount, patientId)
    yield put(createPatientAccountSuccessAction(patient))
  } catch (exception) {
    yield put(createPatientAccountFailureAction(handleError(exception)))
  }
}

function* getPatient(action: PayloadAction<string>) {
  try {
    const patientId = action.payload
    const consultation: Patient = yield call(patientModel.getPatient, patientId)
    yield put(getPatientSuccessAction(consultation))
  } catch (exception) {
    console.warn(exception)
    yield put(getPatientFailureAction(handleError(exception)))
  }
}

function* getPatients(action: PayloadAction<SearchPatientsFilterParams>) {
  try {
    const params = action.payload
    const { items, totalCount }: SearchResponse<Patient> = yield call(patientModel.getPatients, params)
    yield put(getPatientsSuccessAction({ items, totalCount }))
  } catch (exception) {
    console.warn(exception)
    yield put(getPatientsFailureAction(handleError(exception)))
  }
}

function* updatePatient(action: PayloadAction<{ filter: string; update: UpdatePatientDto }>) {
  try {
    const patientId = action.payload.filter
    const updatePatientDto = action.payload.update
    const patient: Patient = yield call(patientModel.updatePatient, patientId, updatePatientDto)
    yield put(updatePatientSuccessAction(patient))
    yield put(patientUpdatedSocketEvent(patient))
  } catch (exception) {
    console.warn(exception)
    yield put(updatePatientFailureAction(handleError(exception)))
  }
}

function* deletePatient(action: PayloadAction<string>) {
  try {
    const patientId = action.payload
    yield call(patientModel.deletePatient, patientId)
    yield put(deletePatientSuccessAction(patientId))
  } catch (exception) {
    console.warn(exception)
    yield put(deletePatientFailureAction(handleError(exception)))
  }
}

function* downloadConsent(action: PayloadAction<string>) {
  try {
    const patientId = action.payload
    const consentDataUrl: string = yield call(patientModel.downloadConsent, patientId)
    yield put(downloadConsentSuccessAction({ patientId, consentDataUrl }))
  } catch (exception) {
    console.warn(exception)
    yield put(downloadConsentFailureAction(handleError(exception)))
  }
}

function* getOrCreateOrdoclicPatient(action: PayloadAction<ReturnType<typeof getOrCreateOrdoclicPatientAction>['payload']>) {
  try {
    const patientId = action.payload
    const patient: SagaReturnType<typeof patientModel.getOrCreateOrdoclicPatient> = yield call(patientModel.getOrCreateOrdoclicPatient, patientId)
    yield put(getOrCreateOrdoclicPatientSuccessAction(patient))
  } catch (exception) {
    yield put(getOrCreateOrdoclicPatientFailureAction(handleError(exception)))
  }
}

const sagas = [
  takeLatest(getCarteVitaleInfosAction.type, getCarteVitaleInfos),
  takeLatest(createPatientAction.type, createPatient),
  takeLatest(createPatientAccountAction.type, createPatientAccount),
  takeLatest(getPatientAction.type, getPatient),
  takeLatest(getPatientsAction.type, getPatients),
  takeLatest(updatePatientAction.type, updatePatient),
  takeLatest(deletePatientAction.type, deletePatient),
  takeLatest(downloadConsentAction.type, downloadConsent),
  takeLatest(getOrCreateOrdoclicPatientAction.type, getOrCreateOrdoclicPatient),
]

export default sagas
